import { HttpMethods, HttpStatusCodes } from './HttpConstants';
import { HttpErrorCodes } from './HttpErrors';

//#region Http Headers

export interface HttpHeadersJson {
	[key: string]: string;
}

export class HttpHeaders {
	private headers: Map<string, string>;

	constructor(headers: HttpHeadersJson = {}) {
		this.headers = Object.keys(headers).reduce(
			(map, key) => map.set(key, headers[key]),
			new Map<string, string>()
		);
	}

	toJSON(): HttpHeadersJson {
		const obj: HttpHeadersJson = {};
		this.headers.forEach((value, key) => (obj[key] = value));
		return obj;
	}

	toString(): string {
		return JSON.stringify(this.toJSON());
	}

	updateHeader(key: string, value: string): void {
		this.headers.set(key, value);
	}
	get(key: string): string | undefined {
		let keyValue: string;
		this.headers.forEach((value, headerKey) => {
			if (headerKey.toLowerCase() === key.toLowerCase()) {
				keyValue = value;
			}
		});
		return keyValue;
	}
}

//#endregion

//#region Http Requests

export interface HttpRequestJson {
	method: HttpMethods;
	url: URL;
	headers: HttpHeaders;
	body: string;
	includeClientCertIfNeeded?: boolean;
	timeout?: number; //timeout in milliseconds
}

export class HttpRequest {
	method: HttpMethods;
	url: URL;
	headers: HttpHeaders;
	body: string;
	includeClientCertIfNeeded?: boolean;
	timeout?: number; //timeout in milliseconds

	constructor(
		method: HttpMethods,
		url: string | URL,
		headers: HttpHeaders | null,
		body: string,
		includeClientCertIfNeeded?: boolean,
		timeout?: number
	) {
		this.method = method;
		this.url = url instanceof URL ? url : new URL(url);
		this.headers = headers;
		this.body = body;
		this.includeClientCertIfNeeded = includeClientCertIfNeeded;
		this.timeout = timeout;
	}

	toJSON(): HttpRequestJson {
		const { method, url, headers, body, includeClientCertIfNeeded, timeout } = this;
		return {
			method,
			url,
			headers,
			body,
			includeClientCertIfNeeded,
			timeout,
		};
	}

	toString(): string {
		return JSON.stringify(this.toJSON());
	}
}

//#endregion

//#region Http Responses

export interface HttpResponseJson {
	statusCode: HttpStatusCodes;
	headers: HttpHeadersJson;
	body: string;
}

export interface HttpErrorJson {
	errorCode: HttpErrorCodes;
	errorDescription: string;
}

export interface HeadersJson {
	[key: string]: string;
}

export interface HTTPFetchResponse {
	statusCode: number;
	body: string;
	headers: HeadersJson;
}

export class HttpResponse {
	statusCode: HttpStatusCodes;
	headers: HttpHeaders;
	body: string;

	constructor(responseJson: HttpResponseJson, expectedStatusCode?: HttpStatusCodes) {
		if (Object.keys(responseJson).length === 0) {
			throw new Error('Empty Response from Native');
		}
		if (expectedStatusCode && responseJson.statusCode !== expectedStatusCode) {
			throw new Error(`Unexpected Status Code: ${responseJson.statusCode}`);
		}
		this.statusCode = responseJson.statusCode;
		this.headers = new HttpHeaders(responseJson.headers);
		this.body = responseJson.body;
	}

	toString(): string {
		return JSON.stringify({
			statusCode: this.statusCode,
			headers: this.headers,
			body: this.body,
		});
	}
}

//#endregion
